/**
 * flowDiagramService.js
 *
 * Firestore CRUD service for UNS architecture flow diagram documents.
 *
 * Collection: `unsFlowData`
 * Document ID: factoryId  (e.g. "tHktnIRtcSYdmjTa0CZe")
 *
 * Document schema
 * ──────────────────────────────────────────────────────────────
 * {
 *   id          : string          — factory / diagram ID (mirrors doc id)
 *   title       : string          — human-readable name
 *   description : string
 *   flow        : FlowNode[]      — ordered array of ReactFlow node definitions
 *   backgroundBoxes : BackgroundBox[] — optional background decoration nodes
 *   createdAt   : Timestamp
 *   updatedAt   : Timestamp
 *   updatedBy   : string          — uid of the last editor
 * }
 *
 * FlowNode schema (mirrors existing flowDataArray items)
 * ──────────────────────────────────────────────────────────────
 * {
 *   id             : string
 *   source         : string
 *   target?        : string
 *   img            : string       — URL to node image
 *   topic          : string | string[]
 *   protocol?      : string
 *   sourcePosition : string       — "left"|"right"|"top"|"bottom"
 *   targetPosition?: string
 *   sourceOverride?: string
 *   nodeType       : string       — "UnsFlowNode" | "UnsBackgroundBox"
 *   active         : boolean
 *   position       : { x: number, y: number }
 * }
 *
 * BackgroundBox schema  (flat Firestore storage — UnsFlow maps to ReactFlow format on read)
 * ──────────────────────────────────────────────────────────────
 * {
 *   id        : string
 *   position  : { x: number, y: number }
 *   label     : string
 *   imageUrl  : string
 *   width     : number
 *   height    : number
 * }
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
} from 'firebase/firestore';
import { db } from '../firebase/config';

// ─── Constants ──────────────────────────────────────────────────────────────

export const FLOW_DIAGRAMS_COLLECTION = 'unsFlowData';

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Returns a Firestore document reference for the given factory / diagram ID.
 */
const diagramRef = (factoryId) =>
  doc(db, FLOW_DIAGRAMS_COLLECTION, factoryId);

/**
 * Returns a Firestore collection reference for all flow diagrams.
 */
const diagramsCollectionRef = () =>
  collection(db, FLOW_DIAGRAMS_COLLECTION);

// ─── READ ────────────────────────────────────────────────────────────────────

/**
 * One-time fetch of a single flow diagram document.
 *
 * @param {string} factoryId
 * @returns {Promise<object|null>} diagram data or null if not found
 */
export const getFlowDiagram = async (factoryId) => {
  try {
    const snap = await getDoc(diagramRef(factoryId));
    if (!snap.exists()) return null;
    return { ...snap.data(), id: snap.id };
  } catch (err) {
    console.error('[flowDiagramService] getFlowDiagram error:', err);
    throw err;
  }
};

/**
 * One-time fetch of ALL flow diagram documents.
 *
 * @returns {Promise<object[]>}
 */
export const getAllFlowDiagrams = async () => {
  try {
    const snap = await getDocs(diagramsCollectionRef());
    return snap.docs.map((d) => ({ ...d.data(), id: d.id }));
  } catch (err) {
    console.error('[flowDiagramService] getAllFlowDiagrams error:', err);
    throw err;
  }
};

/**
 * Real-time listener for a single flow diagram.
 * Feeds the live MQTT topic-highlighting pipeline:
 *   Firestore diagram doc → callback → UnsFlow nodes/edges
 *
 * @param {string}   factoryId
 * @param {Function} onData    — called with `diagram | null` whenever the doc changes
 * @param {Function} [onError] — optional error handler
 * @returns {Function} unsubscribe — call to stop listening
 */
export const subscribeToFlowDiagram = (factoryId, onData, onError) => {
  const unsubscribe = onSnapshot(
    diagramRef(factoryId),
    (snap) => {
      if (snap.exists()) {
        onData({ ...snap.data(), id: snap.id });
      } else {
        onData(null);
      }
    },
    (err) => {
      console.error('[flowDiagramService] subscribeToFlowDiagram error:', err);
      if (onError) onError(err);
    }
  );
  return unsubscribe;
};

/**
 * Real-time listener for ALL flow diagrams.
 * Useful in the admin diagram editor to list / select factory diagrams.
 *
 * @param {Function} onData    — called with `diagram[]` on each update
 * @param {Function} [onError]
 * @returns {Function} unsubscribe
 */
export const subscribeToAllFlowDiagrams = (onData, onError) => {
  const unsubscribe = onSnapshot(
    diagramsCollectionRef(),
    (snap) => {
      const diagrams = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
      onData(diagrams);
    },
    (err) => {
      console.error('[flowDiagramService] subscribeToAllFlowDiagrams error:', err);
      if (onError) onError(err);
    }
  );
  return unsubscribe;
};

// ─── WRITE ───────────────────────────────────────────────────────────────────

/**
 * Create (or overwrite) a flow diagram document.
 * Uses setDoc with merge:false so the full document is replaced.
 *
 * @param {string} factoryId
 * @param {object} diagramData  — { title, description, flow, backgroundBoxes? }
 * @param {string} userId       — uid of the authenticated admin
 * @returns {Promise<void>}
 */
export const createFlowDiagram = async (factoryId, diagramData, userId) => {
  try {
    await setDoc(diagramRef(factoryId), {
      ...diagramData,
      id: factoryId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: userId || null,
    });
  } catch (err) {
    console.error('[flowDiagramService] createFlowDiagram error:', err);
    throw err;
  }
};

/**
 * Partially update a flow diagram document (merge).
 * Preserves existing fields that are not included in `updates`.
 *
 * @param {string} factoryId
 * @param {object} updates    — partial diagram fields to update
 * @param {string} userId
 * @returns {Promise<void>}
 */
export const updateFlowDiagram = async (factoryId, updates, userId) => {
  try {
    await updateDoc(diagramRef(factoryId), {
      ...updates,
      updatedAt: serverTimestamp(),
      updatedBy: userId || null,
    });
  } catch (err) {
    console.error('[flowDiagramService] updateFlowDiagram error:', err);
    throw err;
  }
};

/**
 * Replace the entire `flow` node array for a diagram.
 *
 * @param {string}   factoryId
 * @param {object[]} flowNodes  — complete new array of FlowNode objects
 * @param {string}   userId
 * @returns {Promise<void>}
 */
export const replaceFlowNodes = async (factoryId, flowNodes, userId) => {
  try {
    await updateDoc(diagramRef(factoryId), {
      flow: flowNodes,
      updatedAt: serverTimestamp(),
      updatedBy: userId || null,
    });
  } catch (err) {
    console.error('[flowDiagramService] replaceFlowNodes error:', err);
    throw err;
  }
};

/**
 * Append a single new node to the `flow` array using Firestore arrayUnion.
 * Note: arrayUnion performs a deep-equality check — duplicate objects are ignored.
 * For guaranteed uniqueness use replaceFlowNodes or addFlowNodeById instead.
 *
 * @param {string} factoryId
 * @param {object} nodeData   — FlowNode object to add
 * @param {string} userId
 * @returns {Promise<void>}
 */
export const addFlowNode = async (factoryId, nodeData, userId) => {
  try {
    await updateDoc(diagramRef(factoryId), {
      flow: arrayUnion(nodeData),
      updatedAt: serverTimestamp(),
      updatedBy: userId || null,
    });
  } catch (err) {
    console.error('[flowDiagramService] addFlowNode error:', err);
    throw err;
  }
};

/**
 * Update a single node within the `flow` array by its `id` field.
 * This performs a read-modify-write to replace only the matching node.
 *
 * @param {string} factoryId
 * @param {string} nodeId     — the `id` field of the target FlowNode
 * @param {object} nodeUpdate — partial or full replacement fields
 * @param {string} userId
 * @returns {Promise<void>}
 */
export const updateFlowNode = async (factoryId, nodeId, nodeUpdate, userId) => {
  try {
    const snap = await getDoc(diagramRef(factoryId));
    if (!snap.exists()) throw new Error(`Flow diagram "${factoryId}" not found`);

    const data = snap.data();
    const updatedFlow = (data.flow || []).map((node) =>
      node.id === nodeId ? { ...node, ...nodeUpdate } : node
    );

    await updateDoc(diagramRef(factoryId), {
      flow: updatedFlow,
      updatedAt: serverTimestamp(),
      updatedBy: userId || null,
    });
  } catch (err) {
    console.error('[flowDiagramService] updateFlowNode error:', err);
    throw err;
  }
};

/**
 * Remove a single node from the `flow` array by its `id` field.
 *
 * @param {string} factoryId
 * @param {string} nodeId
 * @param {string} userId
 * @returns {Promise<void>}
 */
export const deleteFlowNode = async (factoryId, nodeId, userId) => {
  try {
    const snap = await getDoc(diagramRef(factoryId));
    if (!snap.exists()) throw new Error(`Flow diagram "${factoryId}" not found`);

    const data = snap.data();
    const updatedFlow = (data.flow || []).filter((node) => node.id !== nodeId);

    await updateDoc(diagramRef(factoryId), {
      flow: updatedFlow,
      updatedAt: serverTimestamp(),
      updatedBy: userId || null,
    });
  } catch (err) {
    console.error('[flowDiagramService] deleteFlowNode error:', err);
    throw err;
  }
};

/**
 * Replace the entire `backgroundBoxes` array for a diagram.
 *
 * @param {string}   factoryId
 * @param {object[]} backgroundBoxes
 * @param {string}   userId
 * @returns {Promise<void>}
 */
export const updateBackgroundBoxes = async (
  factoryId,
  backgroundBoxes,
  userId
) => {
  try {
    await updateDoc(diagramRef(factoryId), {
      backgroundBoxes,
      updatedAt: serverTimestamp(),
      updatedBy: userId || null,
    });
  } catch (err) {
    console.error('[flowDiagramService] updateBackgroundBoxes error:', err);
    throw err;
  }
};

/**
 * Delete an entire flow diagram document.
 *
 * @param {string} factoryId
 * @returns {Promise<void>}
 */
export const deleteFlowDiagram = async (factoryId) => {
  try {
    await deleteDoc(diagramRef(factoryId));
  } catch (err) {
    console.error('[flowDiagramService] deleteFlowDiagram error:', err);
    throw err;
  }
};

// ─── UPSERT ──────────────────────────────────────────────────────────────────

/**
 * Create the diagram document if it does not exist, or merge updates into it
 * if it already does.  This mirrors the `setDoc(..., { merge: true })` pattern
 * used by unsConfig writes in mqttMigration.js, making it safe to call from
 * the admin editor on first load without overwriting existing node data.
 *
 * @param {string} factoryId
 * @param {object} diagramData  — { title?, description?, flow?, backgroundBoxes? }
 * @param {string} userId
 * @returns {Promise<void>}
 */
export const upsertFlowDiagram = async (factoryId, diagramData, userId) => {
  try {
    const snap = await getDoc(diagramRef(factoryId));
    if (snap.exists()) {
      // Document exists — partial merge update
      await updateDoc(diagramRef(factoryId), {
        ...diagramData,
        updatedAt: serverTimestamp(),
        updatedBy: userId || null,
      });
    } else {
      // Document does not exist — full creation
      await setDoc(diagramRef(factoryId), {
        ...diagramData,
        id: factoryId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: userId || null,
      });
    }
  } catch (err) {
    console.error('[flowDiagramService] upsertFlowDiagram error:', err);
    throw err;
  }
};

// ─── BACKGROUND BOX INDIVIDUAL CRUD ──────────────────────────────────────────

/**
 * Append a single new background box to the `backgroundBoxes` array.
 * Performs a read-modify-write so the box is appended without replacing others.
 *
 * @param {string} factoryId
 * @param {object} boxData   — BackgroundBox object (must have a unique `id`)
 * @param {string} userId
 * @returns {Promise<void>}
 */
export const addBackgroundBox = async (factoryId, boxData, userId) => {
  try {
    const snap = await getDoc(diagramRef(factoryId));
    if (!snap.exists()) throw new Error(`Flow diagram "${factoryId}" not found`);

    const data = snap.data();
    const existingBoxes = data.backgroundBoxes || [];

    // Guard against duplicate IDs
    if (existingBoxes.some((b) => b.id === boxData.id)) {
      throw new Error(
        `[flowDiagramService] addBackgroundBox: box id "${boxData.id}" already exists. ` +
          'Use updateBackgroundBox to update it.'
      );
    }

    await updateDoc(diagramRef(factoryId), {
      backgroundBoxes: [...existingBoxes, boxData],
      updatedAt: serverTimestamp(),
      updatedBy: userId || null,
    });
  } catch (err) {
    console.error('[flowDiagramService] addBackgroundBox error:', err);
    throw err;
  }
};

/**
 * Update a single background box within the `backgroundBoxes` array by its `id`.
 * Performs a read-modify-write to replace only the matching box.
 *
 * @param {string} factoryId
 * @param {string} boxId      — the `id` field of the target BackgroundBox
 * @param {object} boxUpdate  — partial or full replacement fields
 * @param {string} userId
 * @returns {Promise<void>}
 */
export const updateBackgroundBox = async (factoryId, boxId, boxUpdate, userId) => {
  try {
    const snap = await getDoc(diagramRef(factoryId));
    if (!snap.exists()) throw new Error(`Flow diagram "${factoryId}" not found`);

    const data = snap.data();
    const updatedBoxes = (data.backgroundBoxes || []).map((box) =>
      box.id === boxId ? { ...box, ...boxUpdate } : box
    );

    await updateDoc(diagramRef(factoryId), {
      backgroundBoxes: updatedBoxes,
      updatedAt: serverTimestamp(),
      updatedBy: userId || null,
    });
  } catch (err) {
    console.error('[flowDiagramService] updateBackgroundBox error:', err);
    throw err;
  }
};

/**
 * Remove a single background box from the `backgroundBoxes` array by its `id`.
 *
 * @param {string} factoryId
 * @param {string} boxId
 * @param {string} userId
 * @returns {Promise<void>}
 */
export const deleteBackgroundBox = async (factoryId, boxId, userId) => {
  try {
    const snap = await getDoc(diagramRef(factoryId));
    if (!snap.exists()) throw new Error(`Flow diagram "${factoryId}" not found`);

    const data = snap.data();
    const updatedBoxes = (data.backgroundBoxes || []).filter((box) => box.id !== boxId);

    await updateDoc(diagramRef(factoryId), {
      backgroundBoxes: updatedBoxes,
      updatedAt: serverTimestamp(),
      updatedBy: userId || null,
    });
  } catch (err) {
    console.error('[flowDiagramService] deleteBackgroundBox error:', err);
    throw err;
  }
};

// ─── BATCH / MIGRATION HELPER ────────────────────────────────────────────────

/**
 * Seed Firestore with the provided array of diagram objects.
 * Useful for migrating the existing hard-coded `flowDataArray` to Firestore.
 *
 * Each item must have an `id` field that becomes the document ID.
 *
 * @param {object[]} diagrams  — array of diagram objects (same shape as flowDataArray)
 * @param {string}   userId    — uid of the admin performing the migration
 * @returns {Promise<void>}
 */
export const seedFlowDiagrams = async (diagrams, userId) => {
  try {
    const writes = diagrams.map((diagram) =>
      setDoc(
        diagramRef(diagram.id),
        {
          ...diagram,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          updatedBy: userId || null,
        },
        { merge: true }
      )
    );
    await Promise.all(writes);
    console.log(
      `[flowDiagramService] Seeded ${diagrams.length} flow diagrams to Firestore.`
    );
  } catch (err) {
    console.error('[flowDiagramService] seedFlowDiagrams error:', err);
    throw err;
  }
};
